import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Bot, MessageSquare } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";

const PERSONAS = { interested: "Interested customer", busy: "Busy, wants a call back", not_interested: "Not interested", opt_out: "Asks never to be called",
  questions: "Asks lots of questions", hindi: "Prefers Hindi", wrong_number: "Wrong number", off_topic: "Asks something off-topic" };

function Bubbles({ msgs, id }) {
  return (
    <div className="max-h-80 space-y-1.5 overflow-y-auto rounded-lg bg-slate-50 p-2" data-testid={id}>
      {msgs.map((m, i) => <p key={i} className={`max-w-[85%] rounded-lg px-2 py-1 text-xs ${m.who === "shally" ? "bg-violet-100 text-violet-950" : "ml-auto bg-white text-slate-800"}`}>
        <b>{m.who === "shally" ? "Shally" : "Customer"}:</b> {m.text}</p>)}
    </div>
  );
}

function Manual({ cid }) {
  const [s, setS] = useState(null), [msg, setMsg] = useState(""), [busy, setBusy] = useState(false);
  const send = (start) => {
    setBusy(true);
    api.post(`/outbound/campaigns/${cid}/practice/manual`, start ? {} : { session_id: s.session_id, message: msg })
      .then((r) => { setS(r.data); setMsg(""); }).catch((e) => toast.error(apiErr(e))).finally(() => setBusy(false));
  };
  return (
    <div className="space-y-2 rounded-lg border border-slate-200 bg-white p-3" data-testid="ob-practice-manual">
      <p className="flex items-center gap-1 text-xs font-semibold"><MessageSquare className="h-4 w-4" />You play the customer</p>
      {!s ? <Button size="sm" onClick={() => send(true)} disabled={busy} data-testid="ob-practice-start">{busy ? "Connecting…" : "Start practice call"}</Button> : <>
        <Bubbles msgs={s.messages} id="ob-practice-chat" />
        {s.end ? <p className="text-xs" data-testid="ob-practice-ended">Call ended · outcome: <b>{s.outcome || "none"}</b>
          <Button size="sm" variant="link" onClick={() => setS(null)} data-testid="ob-practice-new">New practice</Button></p> :
          <div className="flex gap-2">
            <input value={msg} onChange={(e) => setMsg(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && msg.trim() && !busy) { send(false); } }}
              placeholder="Reply as the customer (English or Hindi)…" className="flex-1 rounded border px-2 py-1 text-sm" data-testid="ob-practice-input" />
            <Button size="sm" onClick={() => send(false)} disabled={busy || !msg.trim()} data-testid="ob-practice-send">{busy ? "…" : "Send"}</Button>
          </div>}
      </>}
    </div>
  );
}

function Simulated({ cid }) {
  const [p, setP] = useState("interested"), [lang, setLang] = useState("English"), [busy, setBusy] = useState(false), qc = useQueryClient();
  const run = () => {
    setBusy(true);
    api.post(`/outbound/campaigns/${cid}/practice/simulate`, { persona: p, language: lang })
      .then(() => qc.invalidateQueries({ queryKey: ["ob", "practice", cid] })).catch((e) => toast.error(apiErr(e))).finally(() => setBusy(false));
  };
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-lg border border-slate-200 bg-white p-3 text-xs" data-testid="ob-practice-sim">
      <Bot className="h-4 w-4" />AI plays the customer:
      <select value={p} onChange={(e) => setP(e.target.value)} className="rounded border px-2 py-1" data-testid="ob-sim-persona">
        {Object.entries(PERSONAS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
      <select value={lang} onChange={(e) => setLang(e.target.value)} className="rounded border px-2 py-1" data-testid="ob-sim-lang">
        {["English", "Hindi", "Hinglish"].map((l) => <option key={l}>{l}</option>)}</select>
      <Button size="sm" onClick={run} disabled={busy} data-testid="ob-sim-run">{busy ? "Simulating… (up to a minute)" : "Run practice call"}</Button>
    </div>
  );
}

export function PracticePanel({ camp }) {
  const q = useQuery({ queryKey: ["ob", "practice", camp.id], queryFn: async () => (await api.get(`/outbound/campaigns/${camp.id}/practice`)).data });
  const sims = (q.data || []).filter((x) => x.mode === "simulated");
  return (
    <div className="space-y-3" data-testid="ob-practice">
      <p className="text-xs text-slate-600">Text-only practice: no phone call and no call charges. It uses the approved strategy, or the campaign brief if none is approved yet.</p>
      <Manual cid={camp.id} />
      <Simulated cid={camp.id} />
      {sims.map((x) => (
        <div key={x.id} className="space-y-1 rounded-lg border border-slate-200 bg-white p-3 text-xs" data-testid={`ob-sim-${x.id}`}>
          <p><b>{PERSONAS[x.persona]}</b> · {x.language} · outcome <b data-testid={`ob-sim-outcome-${x.id}`}>{x.outcome}</b></p>
          <Bubbles msgs={x.messages} id={`ob-sim-chat-${x.id}`} />
          <p className="text-slate-600">{x.summary}</p>
          {x.unsupported_claims?.length > 0 && <p className="text-rose-700" data-testid={`ob-sim-flags-${x.id}`}>Check these statements: {x.unsupported_claims.join("; ")}</p>}
        </div>))}
    </div>
  );
}
